import masterServices from '../../../../services/oneButtonServices/masterServices';
import quoteServices from '../../../../services/oneButtonServices/quoteServices';

export type EnfinEscalatorOption = { value: string; label: string };

/** Trinity solar form `Escalator` for GetEnfinPricing — matches QuotesContractsCard / SolarQuoteDetails. */
export function getEnfinEscalatorRateFromPriceQuote(priceQuote: any): string {
    const solar = priceQuote?.Solar;
    const raw =
        solar?.Escalator ??
        priceQuote?.RequestedPPARateEscalator ??
        solar?.PaymentEscalator ??
        '0';
    return String(raw).replace(/%/g, '').trim() || '0';
}

export function isEnfinSolarFinancePartner(opportunityDetails: any | null | undefined): boolean {
    const products = opportunityDetails?.OpportunityProduct || [];
    const solar = products.find((p: any) => p.Product_Type_Name === 'Solar' && p.ActiveFlag);
    if (!solar) return false;
    const partner = String(solar.Finance_Partner_Display_Name || solar.Finance_Partner || '').toLowerCase();
    return partner === 'enfin';
}

/** Show Enfin eligible-products tooling when Solar is on the deal and Enfin is the solar finance partner. */
export function shouldShowEnfinEligibleProducts(
    opportunityDetails: any | null | undefined,
    priceQuote: any | null | undefined,
): boolean {
    const interest = opportunityDetails?.OpportunityInterest;
    return !!(priceQuote && interest?.Solar && isEnfinSolarFinancePartner(opportunityDetails));
}

export function getEnfinSolarFinanceMethod(opportunityDetails: any | null | undefined): string {
    const products = opportunityDetails?.OpportunityProduct || [];
    const solar = products.find((p: any) => p.Product_Type_Name === 'Solar' && p.ActiveFlag);
    if (!solar) return '';
    return String(
        solar.Finance_Method_Name ?? solar.FinanceMethod ?? solar.Finance_Method ?? '',
    ).trim();
}

/** SVA Financing Options C4 for Enfin eligible-products escalator Excel call. */
export function getEnfinSvaC4(
    opportunityDetails: any | null | undefined,
    productType: string | null | undefined,
): string {
    if (productType === 'SB') return 'SB';
    const interest = opportunityDetails?.OpportunityInterest;
    if (interest?.Solar && interest?.Battery && !interest?.BatteryOnly) return 'SB';
    return 'S';
}

export function shouldFetchEnfinBatteryPricing(
    opportunityDetails: any | null | undefined,
    productType: string | null | undefined,
): boolean {
    if (productType === 'SB') return true;
    const interest = opportunityDetails?.OpportunityInterest;
    return !!(interest?.Solar && interest?.Battery && !interest?.BatteryOnly);
}

/**
 * Escalator dropdown options — same Excel cascade as `SolarQuoteDetails.getSolarQuoteFormValues`
 * (Sunnova Financial Data / SVA Financing Options, output column index 3).
 */
export async function fetchEnfinEscalatorOptions(args: {
    priceQuote: any;
    financeMethod: string;
    svaC4: string;
    /** Enfin rate-based pricing; defaults to Solar Rate. */
    pricingMethodText?: string;
}): Promise<EnfinEscalatorOption[]> {
    const { priceQuote, financeMethod, svaC4, pricingMethodText = 'Solar Rate' } = args;
    const installationState = priceQuote?.InstallationState ?? '';
    const excelUtilityCompanyName = priceQuote?.ExcelUtilityCompanyName ?? '';

    const response = await masterServices.cascadingDropDown({
        ExcelFileName: 'Sunnova Financial Data',
        SheetName: 'SVA Financing Options',
        InputValues: [
            { Type: 'String', CellNumber: 'B4', CellValue: installationState },
            { Type: 'String', CellNumber: 'C4', CellValue: svaC4 },
            { Type: 'String', CellNumber: 'D4', CellValue: 'Enfin' },
            { Type: 'String', CellNumber: 'F4', CellValue: financeMethod },
            { Type: 'String', CellNumber: 'C8', CellValue: '' },
            { Type: 'String', CellNumber: 'E8', CellValue: excelUtilityCompanyName },
            { Type: 'String', CellNumber: 'K4', CellValue: pricingMethodText },
        ],
        OutputCells: ['G4', 'N4', 'H4', 'L4', 'I4', 'G8', 'T4', 'N11'],
    });

    const cellValue = response?.[3]?.CellValue;
    if (cellValue == null || String(cellValue).trim() === '') return [];

    return String(cellValue)
        .split('|')
        .map((ele) => ele.trim())
        .filter(Boolean)
        .map((text) => ({ value: text, label: text }));
}

export function resolveDefaultEnfinEscalator(
    priceQuote: any,
    options: EnfinEscalatorOption[],
): string {
    if (options.length === 0) return '';
    const preferred = getEnfinEscalatorRateFromPriceQuote(priceQuote);
    const match = options.find((o) => o.value.trim() === preferred.trim());
    return match?.value ?? options[0].value;
}

export function normalizeEnfinPricingRows(raw: unknown): Record<string, unknown>[] {
    if (Array.isArray(raw)) return raw as Record<string, unknown>[];
    if (raw && typeof raw === 'object') {
        const o = raw as Record<string, unknown>;
        const details = o.pricingOfferDetails ?? o.PricingOfferDetails;
        if (Array.isArray(details)) return details as Record<string, unknown>[];
    }
    return [];
}

export function filterEnfinPricingRows(rows: Record<string, unknown>[]): Record<string, unknown>[] {
    return rows.filter((row) => row.totalkwhrate != null);
}

export function sortEnfinPricingRows(rows: Record<string, unknown>[]): Record<string, unknown>[] {
    return [...rows].sort((a, b) => {
        const fields = ['totalkwhrate', 'monthlyPayment', 'epcPaymentPerWatt'];
        for (const field of fields) {
            const va = Number(a[field]) || 0;
            const vb = Number(b[field]) || 0;
            if (va !== vb) return va - vb;
        }
        return 0;
    });
}

export function processEnfinPricingResponse(raw: unknown): Record<string, unknown>[] {
    return sortEnfinPricingRows(filterEnfinPricingRows(normalizeEnfinPricingRows(raw)));
}

export async function fetchEnfinEligiblePricingRows(args: {
    opportunityId: string | number;
    escalatorRate: string | number;
    fetchBatteryPricing: boolean;
}): Promise<{ solarRows: Record<string, unknown>[]; batteryRows: Record<string, unknown>[] }> {
    const basePayload = {
        opportunityId: args.opportunityId,
        escalatorRate: args.escalatorRate,
        term: '25',
    };

    const solarRes = await quoteServices.getEnfinPriceCaps(basePayload);
    const solarRows = processEnfinPricingResponse(solarRes);

    let batteryRows: Record<string, unknown>[] = [];
    if (args.fetchBatteryPricing) {
        const batteryRes = await quoteServices.getEnfinPriceCaps({
            ...basePayload,
            batteryMode: true,
        });
        batteryRows = processEnfinPricingResponse(batteryRes);
    }

    return { solarRows, batteryRows };
}
